import * as React from "react"
import { Tooltip } from "@/components/ui/tooltip"

/**
 * AR-210 (TC-09.1): a Radix Tooltip that also closes when focus leaves its
 * trigger, the pointer leaves the document, or the window loses focus. Radix
 * otherwise waits for a later pointer move, so a hover-opened help tooltip
 * could linger over neighbouring content (WCAG 1.4.13). Drop-in for <Tooltip>.
 */
export function DismissibleTooltip({ children, delayDuration }: { children: React.ReactNode; delayDuration?: number }) {
  const [open, setOpen] = React.useState(false)
  const wrapRef = React.useRef<HTMLSpanElement>(null)
  React.useEffect(() => {
    if (!open) return
    const close = () => setOpen(false)
    const onFocusIn = (e: FocusEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) close()
    }
    const onPointerMove = (e: PointerEvent) => {
      const trigger = wrapRef.current?.firstElementChild
      const content = document.querySelector("[data-radix-popper-content-wrapper]")
      const t = e.target as Node
      if (trigger && !trigger.contains(t) && !(content && content.contains(t))) close()
    }
    document.addEventListener("focusin", onFocusIn)
    document.addEventListener("pointermove", onPointerMove)
    document.documentElement.addEventListener("mouseleave", close)
    window.addEventListener("blur", close)
    return () => {
      document.removeEventListener("focusin", onFocusIn)
      document.removeEventListener("pointermove", onPointerMove)
      document.documentElement.removeEventListener("mouseleave", close)
      window.removeEventListener("blur", close)
    }
  }, [open])
  return (
    <span ref={wrapRef} className="contents">
      <Tooltip open={open} onOpenChange={setOpen} delayDuration={delayDuration}>
        {children}
      </Tooltip>
    </span>
  )
}
